import { call, put, takeEvery } from "redux-saga/effects";
import * as types from "./strategicdemandactionType";
import * as actions from "./strategicdemandaction";
import * as service from "./strategicdemandservice";
import { downloadBase64File } from "../../../../utils/fileDownloads";
import Swal from "sweetalert2";
import SweetAlert from "../../../../components/sweetalert/SweetAlert";

/* ======================================================
   Volume Trend Report Dropdown
   ====================================================== */
function* fetchVolumeTrendReportsDDSaga() {
  try {
    const response = yield call(service.getVolumeTrendReportsDDList);
    yield put(actions.fetchStrategicVolumeTrendReportsDDSuccess(response));
  } catch (error) {
    yield put(
      actions.fetchStrategicVolumeTrendReportsDDFailure(
        error.message || "Failed to fetch dropdown data"
      )
    );
    SweetAlert.error({
      title: 'Error!',
      text: error?.errorMessage || 'Failed to load Volume Trend Reports Dropdown.',
      showConfirmButton: true
    });
  }
}

/* ======================================================
   Strategic Reports (Reports Tab)
   ====================================================== */
function* fetchStrategicReportsTabSaga(action) {
  try {
    const response = yield call(service.getAllReportsWithPagination, action.payload);

    const values = response.values || [];
    const totalCount = response.recordsTotal || 0;

    yield put(
      actions.fetchStrategicReportsTabSuccess({
        values,
        recordsTotal: totalCount,
      })
    );
  } catch (error) {
    yield put(actions.fetchStrategicReportsTabFailure(error.message));
    SweetAlert.error({
      title: 'Error!',
      text: error?.errorMessage || 'Failed to load Strategic Reports.',
      showConfirmButton: true
    });
  }
}

/* ======================================================
   Export Reports
   ====================================================== */
function* exportReportsSaga(action) {
  try {
    let exportAPIURL, payload, moduleName, fileName;

    if (action.payload?.exportAPIURL) {
      exportAPIURL = action.payload.exportAPIURL;
      payload = action.payload.payload;
      moduleName = action.payload.moduleName || "Strategic Demand Planner";
      fileName = action.payload.fileName;
    } else {
      payload = action.payload;
      moduleName = "Strategic Demand Planner";
      fileName = action.payload.fileName;
    }

    const response = exportAPIURL
      ? yield call(service.exportStrategicReportsData, exportAPIURL, payload)
      : yield call(service.exportStrategicReportsData, payload);

    const base64Data = response?.values || response;

    yield call(downloadBase64File, base64Data, moduleName, fileName);
    yield put(actions.exportStrategicReportsSuccess(response));
  } catch (error) {
    console.error("Export error:", error);
    yield put(actions.exportStrategicReportsFailure("Export failed"));
    SweetAlert.error({
      title: 'Error!',
      text: error.message || 'An error occurred while exporting reports.',
      showConfirmButton: true
    });
  }
}

/* ======================================================
   Delete Strategic Demand Report
   ====================================================== */
function* deleteStrategicDemandReportSaga(action) {
  try {
    const response = yield call(service.deleteStrategicDemandReport, action.payload);
    // yield call(service.deleteStrategicDemandReport, action.payload);
    yield put(actions.deleteStrategicDemandReportSuccess(action.payload));
    // SweetAlert.success({
    //   title: 'Success!',
    //   text: 'Report deleted successfully.',
    //   showConfirmButton: true
    // });
    if (response?.success) {
      yield put(
        actions.deleteStrategicDemandReportSuccess(response)
      );

      SweetAlert.success({
        title: 'Success!',
        text: response?.resultMessage
      });
    }
  } catch (error) {
    yield put(
      actions.deleteStrategicDemandReportFailure(
        error.message || "Delete failed"
      )
    );
    SweetAlert.error({
      title: 'Error!',
      text: error.message || 'An error occurred while deleting the report.',
      showConfirmButton: true
    });
  }
}

/* ======================================================
   Update Report Notes
   ====================================================== */
function* updateBlockPlanningReportNoteSaga(action) {
  try {
    yield call(service.updateBlockPlanningReportNote, action.payload);
    yield put(actions.updateBlockPlanningReportNoteSuccess());
    SweetAlert.success({
      title: 'Success!',
      text: 'Report note updated successfully.',
      showConfirmButton: true
    });
  } catch (error) {
    yield put(actions.updateBlockPlanningReportNoteFailure(error.message));
    SweetAlert.error({
      title: 'Error!',
      text: error.message || 'An error occurred while updating the report note.',
      showConfirmButton: true
    });
  }
}

/* ======================================================
   Block Capacity Table
   ====================================================== */
function* fetchBlockCapacityTableReportSaga(action) {
  try {
    const response = yield call(
      service.getBlockCapacityTableReport,
      action.payload
    );
    yield put(actions.fetchBlockCapacityTableReportSuccess(response));
  } catch (error) {
    yield put(actions.fetchBlockCapacityTableReportFailure(error.message));
    SweetAlert.error({
      title: 'Error!',
      text: error?.errorMessage || 'Failed to load Block Capacity Table Report.',
      showConfirmButton: true
    });
  }
}

/* ======================================================
   Draft / Publish Demand Planning
   ====================================================== */
function* draftBlockDemandPlanningSaga(action) {
  try {
    const response = yield call(
      service.draftBlockDemandPlanningReport,
      action.payload
    );
    yield put({
      type: types.DRAFT_BLOCK_DEMAND_PLANNING_SUCCESS,
      payload: response.data || response,
    });
    SweetAlert.success({
      title: 'Success!',
      text: response?.resultMessage
    });
  } catch (error) {
    yield put({
      type: types.DRAFT_BLOCK_DEMAND_PLANNING_FAILURE,
      payload: error.message || error,
    });
    SweetAlert.error({
      title: 'Error!',
      text: error.message || 'An error occurred while saving the draft.',
      showConfirmButton: true
    });
  }
}

function* publishBlockDemandPlanningSaga(action) {
  try {
    const response = yield call(
      service.publishBlockDemandPlanning,
      action.payload
    );
    yield put({
      type: types.PUBLISH_BLOCK_DEMAND_PLANNING_SUCCESS,
      payload: response.data || response,
    });
    SweetAlert.success({
      title: 'Success!',
      text: response?.resultMessage,
      showConfirmButton: true
    });
  } catch (error) {
    yield put({
      type: types.PUBLISH_BLOCK_DEMAND_PLANNING_FAILURE,
      payload: error.message || error,
    });
    SweetAlert.error({
      title: 'Error!',
      text: error.message || 'An error occurred while publishing.',
      showConfirmButton: true
    });
  }
}

/* ======================================================
   Block Planning Demand Input
   ====================================================== */
function* fetchBlockPlanningDemandInputSaga(action) {
  try {
    const response = yield call(
      service.getBlockPlanningDemandInputWithPagination,
      action.payload
    );
    yield put(actions.fetchBlockPlanningDemandInputSuccess(response));
  } catch (error) {
    yield put(
      actions.fetchBlockPlanningDemandInputFailure(error.message)
    );
    SweetAlert.error({
      title: 'Error!',
      text: error?.errorMessage || 'Failed to load Block Planning Demand Input.',
      showConfirmButton: true
    });
  }
}

/* ======================================================
   Site Onboarding Legend
   ====================================================== */
function* fetchOnboardingSiteLegendSaga() {
  try {
    const response = yield call(
      service.getOnboardingSiteDemandPlannerLegend
    );
    yield put(actions.fetchOnboardingSiteLegendSuccess(response));
  } catch (error) {
    yield put(
      actions.fetchOnboardingSiteLegendFailure(error.message)
    );
    SweetAlert.error({
      title: 'Error!',
      text: error?.errorMessage || 'Failed to load Onboarding Site Legend.',
      showConfirmButton: true
    });
  }
}

/* ======================================================
   Demand Input Filter Dropdowns
   ====================================================== */
function* fetchDemandInputFilterDDSaga(action) {
  try {
    const { reportId } = action.payload;
    const response = yield call(
      service.getDemandInputFilterDDList,
      reportId
    );

    yield put({
      type: types.FETCH_DEMAND_INPUT_FILTER_DD_SUCCESS,
      payload: response.data || response,
    });
  } catch (error) {
    yield put({
      type: types.FETCH_DEMAND_INPUT_FILTER_DD_FAILURE,
      payload: error.message || error,
    });
    SweetAlert.error({
      title: 'Error!',
      text: error.message || 'An error occurred while fetching filter dropdowns.',
      showConfirmButton: true
    });
  }
}
function* fetchWorklistByRPCESaga(action) {
  try {
    // Extract payload from action
    const { rpceIds } = action.payload;

    // Call API with rpceIds
    const response = yield call(service.getWorkListByRPCEIds, rpceIds);

    if (response?.success) {
      yield put(actions.fetchWorklistByRPCESuccess(response.values));
    } else {
      yield put(actions.fetchWorklistByRPCEFailure('No data found'));
      SweetAlert.error({
        title: 'Error!',
        text: response?.errorMessage || 'Failed to load Worklist Provider Facilities.',
        showConfirmButton: true
      });
    }
  } catch (error) {
    console.error('Worklist Provider Facilities Fetch Error:', error);
    yield put(actions.fetchWorklistByRPCEFailure('Failed to fetch Worklist Provider Facilities'));
    SweetAlert.error({
      title: 'Error!',
      text: error.message || 'Failed to load Worklist Provider Facilities.',
      showConfirmButton: true
    });
  }
}

function* fetchPracticeByWorklistSaga(action) {
  try {
    // Extract payload from action
    const { worklistIds } = action.payload;

    // Call API with worklistIds
    const response = yield call(service.getPracticesByWorklistIds, worklistIds);

    if (response?.success) {
      yield put(actions.fetchPracticeByWorklistSuccess(response.values));
    } else {
      yield put(actions.fetchPracticeByWorklistFailure('No data found'));
      SweetAlert.error({
        title: 'Error!',
        text: response?.errorMessage || 'Failed to load Worklist Provider Facilities.',
        showConfirmButton: true
      });
    }
  }
  catch (error) {
    console.error('Worklist Provider Facilities Fetch Error:', error);
    yield put(actions.fetchPracticeByWorklistFailure('Failed to fetch Worklist Provider Facilities'));
    SweetAlert.error({
      title: 'Error!',
      text: error.message || 'Failed to load Worklist Provider Facilities.',
      showConfirmButton: true
    });
  }

}
function* fetchSiteOnboardingPlannerSaga(action) {
  try {
    const response = yield call(
      service.getSiteOnboardingPlanner,
      action.payload
    );

    if (response?.success) {
      yield put(
        actions.fetchSiteOnboardingPlannerSuccess(response)  // Send full response
      );
    } else {
      yield put(
        actions.fetchSiteOnboardingPlannerFailure(
          response?.errorMessage || 'No data found'
        )
      );
      SweetAlert.error({
        title: 'Error!',
        text: response?.errorMessage || 'Failed to load Site Onboarding Planner.',
        showConfirmButton: true
      });
    }
  } catch (error) {
    yield put(
      actions.fetchSiteOnboardingPlannerFailure(
        error.message || 'Failed to load Site Onboarding Planner'
      )
    );
    SweetAlert.error({
      title: 'Error!',
      text: error.message || 'Failed to load Site Onboarding Planner.',
      showConfirmButton: true
    });
  }
}
function* fetchSiteOffboardingPlannerSaga(action) {
  try {
    const response = yield call(
      service.getSiteOffboardingPlanner,  // Your service method
      action.payload
    );

    if (response?.success) {
      yield put(
        actions.fetchSiteOffboardingPlannerSuccess(response)
      );
    } else {
      yield put(
        actions.fetchSiteOffboardingPlannerFailure(
          response?.errorMessage || 'No data found'
        )
      );
      SweetAlert.error({
        title: 'Error!',
        text: response?.errorMessage || 'Failed to load Site Offboarding Planner.',
        showConfirmButton: true
      });
    }
  } catch (error) {
    yield put(
      actions.fetchSiteOffboardingPlannerFailure(
        error.message || 'Failed to load Site Offboarding Planner'
      )
    );
    SweetAlert.error({
      title: 'Error!',
      text: error.message || 'Failed to load Site Offboarding Planner.',
      showConfirmButton: true
    });
  }
}
function* fetchBlockPlanningDemandDailySaga(action) {
  try {
    const response = yield call(
      service.getBlockPlanningDemandDailyWithPagination,
      action.payload
    );

    yield put(
      actions.fetchBlockPlanningDemandDailySuccess(response)
    );
  } catch (error) {
    yield put(
      actions.fetchBlockPlanningDemandDailyFailure(
        error.message || "Failed to load demand daily data"
      )
    );
    SweetAlert.error({
      title: 'Error!',
      text: error.message || 'Failed to load Block Planning Demand Daily data.',
      showConfirmButton: true
    });
  }
}
// strategicdemandsaga.js
function* fetchDemandQuarterlySaga(action) {
  try {
    const response = yield call(
      service.getBlockPlanningDemandQuarterlyWithPagination,
      action.payload
    );

    yield put(actions.fetchDemandQuarterlySuccess(response));
  } catch (error) {
    yield put(actions.fetchDemandQuarterlyFailure(error.message || "Quarterly fetch failed"));
    SweetAlert.error({
      title: 'Error!',
      text: error.message || 'Failed to load Demand Quarterly data.',
      showConfirmButton: true
    });
  }
}

//Fetch Week and Quarter Saga
function* fetchWeekQuarterSaga(action) {
  try {
    const response = yield call(service.GetWeekAndQuarterDetails, action.payload.fromMonthYear, action.payload.toMonthYear);

    yield put(actions.fetchWeekAndQuarterSuccess(response));

  } catch (error) {
    // Dispatch failure action with error
    yield put(actions.fetchWeekAndQuarterFailure(error.message));

    // Show error alert
    Swal.fire({
      icon: 'error',
      title: 'Error!',
      text: error.message || 'An error occurred while fetching data.',
      confirmButtonText: 'OK',
      buttonsStyling: false,
      customClass: { confirmButton: 'btn btn-primary' },
    });
  }
}



// Add to saga watchers


/* ======================================================
   Watcher
   ====================================================== */
export default function* watchStrategicDemandPlanner() {
  yield takeEvery(types.FETCH_VOLUME_TREND_REPORTS_DD_REQUEST, fetchVolumeTrendReportsDDSaga);
  yield takeEvery(types.FETCH_STRATEGIC_REPORTS_TAB_REQUEST, fetchStrategicReportsTabSaga);
  yield takeEvery(types.EXPORT_STRATEGIC_REPORTS_REQUEST, exportReportsSaga);
  yield takeEvery(types.DELETE_STRATEGIC_DEMAND_REPORT_REQUEST, deleteStrategicDemandReportSaga);
  yield takeEvery(types.UPDATE_BLOCK_PLANNING_REPORT_NOTE_REQUEST, updateBlockPlanningReportNoteSaga);
  yield takeEvery(types.FETCH_BLOCK_CAPACITY_TABLE_REPORT_REQUEST, fetchBlockCapacityTableReportSaga);
  yield takeEvery(types.FETCH_BLOCK_PLANNING_DEMAND_INPUT_REQUEST, fetchBlockPlanningDemandInputSaga);
  yield takeEvery(types.FETCH_ONBOARDING_SITE_LEGEND_REQUEST, fetchOnboardingSiteLegendSaga);
  yield takeEvery(types.DRAFT_BLOCK_DEMAND_PLANNING_REQUEST, draftBlockDemandPlanningSaga);
  yield takeEvery(types.PUBLISH_BLOCK_DEMAND_PLANNING_REQUEST, publishBlockDemandPlanningSaga);
  yield takeEvery(types.FETCH_DEMAND_INPUT_FILTER_DD_REQUEST, fetchDemandInputFilterDDSaga);
  yield takeEvery(types.FETCH_WORKLIST_BY_RPCE_DEMAND_REQUEST, fetchWorklistByRPCESaga);
  yield takeEvery(types.FETCH_PRACTICE_BY_WORKLIST_REQUEST, fetchPracticeByWorklistSaga);
  yield takeEvery(types.FETCH_SITE_ONBOARDING_PLANNER_REQUEST, fetchSiteOnboardingPlannerSaga);
  yield takeEvery(types.FETCH_SITE_OFFBOARDING_PLANNER_REQUEST, fetchSiteOffboardingPlannerSaga);
  yield takeEvery(types.FETCH_BLOCK_PLANNING_DEMAND_DAILY_REQUEST, fetchBlockPlanningDemandDailySaga);
  yield takeEvery(types.FETCH_DEMAND_QUARTERLY_REQUEST, fetchDemandQuarterlySaga);
  yield takeEvery(types.FETCH_WEEK_OR_QUARTER_REQUEST, fetchWeekQuarterSaga);

}

